import _JSON$stringify from 'babel-runtime/core-js/json/stringify';
import _getIterator from 'babel-runtime/core-js/get-iterator';
import { Howl, Howler } from '@rosetta/howler';
import silence from 'silent-mp3-datauri'; // A 15ms silence bundled into the app so there's no latency in our check.
import { getPuddleConfig } from '../configs/configs';

export var transparentPxImageSrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export var MEDIA_TYPE = {
  AUDIO: 'audio',
  IMAGE: 'image',
  VIDEO: 'video'
};

export var VIDEO_SIZE = {
  SMALL: 'video_mp4_max_res_360',
  MEDIUM: 'video_mp4_max_res_480'
};

export var IMAGE_SIZE = {
  SMALL: 'picture_max_width_320',
  MEDIUM: 'picture_max_width_640',
  LARGE: 'picture_max_width_1080'
};

var DEFAULT_IMAGE_TYPE = IMAGE_SIZE.SMALL;
var DEFAULT_VIDEO_TYPE = VIDEO_SIZE.MEDIUM;
var DEFAULT_AUDIO_TYPE = 'sound_mp3';

var SMALL_SCREEN_PIXELS = 560;
export var SMALL_SCREEN_MEDIA_STYLE = '@media screen and (max-width: ' + SMALL_SCREEN_PIXELS + 'px)';
export var isSmallScreen = function isSmallScreen() {
  return window.innerWidth <= SMALL_SCREEN_PIXELS;
};

export var isTouchScreen = function isTouchScreen() {
  return 'ontouchstart' in window;
};

// because Edge does not do flat (yet?)
var flatten = function flatten(inArray) {
  if (Array.prototype.flat) {
    return inArray.flat(1);
  }
  var flatArr = [];
  inArray.forEach(function (elem) {
    if (Array.isArray(elem)) {
      for (var _iterator = elem, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
        var _ref;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref = _i.value;
        }

        var subElem = _ref;

        flatArr.push(subElem);
      }
    } else {
      flatArr.push(elem);
    }
  });
  return flatArr;
};

// returns an array of URNs that match mediaDescriptor and mediaType
export var getUrnFromContent = function getUrnFromContent(_ref2) {
  var mediaInfoOrArray = _ref2.mediaInfoOrArray,
      mediaDescriptor = _ref2.mediaDescriptor,
      mediaType = _ref2.mediaType,
      errorHandler = _ref2.errorHandler;

  if (!mediaDescriptor) {
    switch (mediaType) {
      case MEDIA_TYPE.AUDIO:
        mediaDescriptor = DEFAULT_AUDIO_TYPE;
        break;
      case MEDIA_TYPE.IMAGE:
        mediaDescriptor = DEFAULT_IMAGE_TYPE;
        break;
      case MEDIA_TYPE.VIDEO:
        mediaDescriptor = DEFAULT_VIDEO_TYPE;
        break;
      default:
        errorHandler('mediaType must be specified');
        return null;
    }
  }
  if (!mediaInfoOrArray || Array.isArray(mediaInfoOrArray) && mediaInfoOrArray.length === 0) {
    return [];
  }

  var urnArray = [];
  if (Array.isArray(mediaInfoOrArray)) {
    if (mediaType === MEDIA_TYPE.AUDIO) {
      // sometimes the audio is packaged as an array of arrays of single audios
      mediaInfoOrArray = flatten(mediaInfoOrArray);
    }
    urnArray = mediaInfoOrArray.filter(function (mediaItem) {
      return mediaItem.type === mediaDescriptor;
    });
  } else {
    var subArray = void 0;
    switch (mediaType) {
      case MEDIA_TYPE.AUDIO:
        subArray = mediaInfoOrArray.audios;
        break;
      case MEDIA_TYPE.IMAGE:
        subArray = mediaInfoOrArray.images;
        break;
      case MEDIA_TYPE.VIDEO:
        subArray = mediaInfoOrArray.videos;
        break;
      default:
    }
    if (subArray) {
      urnArray = subArray.filter(function (mediaItem) {
        return mediaItem.type === mediaDescriptor;
      });
    } else {
      urnArray = [mediaInfoOrArray];
    }
  }
  return urnArray;
};

/**
 * parseMediaContent()
 *  mediaInfoOrArray - videos, audios, images
 *  mediaDescriptor - the size of the image or video; all audio is currently the same
 *  mediaType - one of MEDIA_TYPE
 *  errorHandler - signature is errorHandler(errStr)
 *  isOverlayImage - boolean
 *  fileExtension - e.g. 'jpg'
 *  isWebi - is the media located on a webi server?
 *
 *  returns an array of object(s) like { media_uri: someURI } or
 *    if it is puddle { encryptedPuddleId: resourceId }
 *    OR null if there is an error in the data
 */
export var parseMediaContent = function parseMediaContent(_ref3) {
  var mediaInfoOrArray = _ref3.mediaInfoOrArray,
      mediaDescriptor = _ref3.mediaDescriptor,
      mediaType = _ref3.mediaType,
      fileExtension = _ref3.fileExtension,
      errorHandler = _ref3.errorHandler,
      isOverlayImage = _ref3.isOverlayImage,
      isWebi = _ref3.isWebi;

  if (!mediaInfoOrArray || Array.isArray(mediaInfoOrArray) && mediaInfoOrArray.length === 0) {
    return null;
  }
  var urnArray = getUrnFromContent({ mediaInfoOrArray: mediaInfoOrArray, mediaDescriptor: mediaDescriptor, mediaType: mediaType, errorHandler: errorHandler });
  if (urnArray.length === 0 || urnArray.some(function (urn) {
    return !urn.media_uri;
  })) {
    errorHandler('missing media_uri: ' + _JSON$stringify(mediaInfoOrArray));
    return null;
  }

  var infoArray = [];
  for (var x = 0; x < urnArray.length; x++) {
    var urn = urnArray[x];
    var splitMediaUrn = urn.media_uri.split(':');
    if (splitMediaUrn.length < 2) {
      errorHandler('invalid media_uri: ' + urn.media_uri);
      return null;
    }
    var urnType = splitMediaUrn[1];
    var resourceId = splitMediaUrn[2];
    if (urnType === 'lts' && splitMediaUrn.length < 6) {
      errorHandler('invalid lts media_uri: ' + urn.media_uri);
      return null;
    }

    // validated the args, now use them to figure out the uri
    switch (urnType) {
      case 'puddle':
        infoArray.push({ encryptedPuddleId: resourceId });
        break;

      case 'puddle_unencrypted':
        var prefixOne = resourceId.substring(0, 2);
        var prefixTwo = resourceId.substring(2, 3);
        var baseUrl = getPuddleConfig().unencryptedPuddleUriRoot;
        infoArray.push({ mediaUri: baseUrl + prefixOne + '/' + prefixTwo + '/' + resourceId });
        break;

      case 'rsa':
        if (mediaType === MEDIA_TYPE.AUDIO) {
          infoArray.push({
            mediaUri: 'https://ms5001.rosettastone.com/MediumHandler.ashx?quality=5&mediumSubtype=0&extension=SNF&id=' + resourceId
          });
        } else if (mediaType === MEDIA_TYPE.IMAGE) {
          var mediaUri = 'https://ms5001.rosettastone.com/MediumHandler.ashx?quality=1&mediumSubtype=0&extension=' + fileExtension;
          if (isOverlayImage) {
            var overlayArray = resourceId.split('-');
            mediaUri += '&zoom=100';
            mediaUri += '&page=' + overlayArray[0];
            mediaUri += '&layer=' + overlayArray[1];
            mediaUri += '&id=' + overlayArray[2];
          } else {
            // normal non-overlay case
            mediaUri += '&id=' + resourceId;
          }
          infoArray.push({ mediaUri: mediaUri });
        } else if (mediaType === MEDIA_TYPE.VIDEO) {
          var base = isWebi ? 'https://ms4001.rosettastone.cn' : 'https://ms5001.rosettastone.com';
          infoArray.push({
            mediaUri: base + '/MediumHandler.ashx?quality=5&mediumSubtype=0&id=' + resourceId + '&extension=mp4'
          });
        }
        break;

      case 'biblio':
        if (mediaType === MEDIA_TYPE.IMAGE) {
          infoArray.push({ mediaUri: 'https://biblio-cache.rosettastone.com/image/' + resourceId });
        } else {
          infoArray.push({ mediaUri: 'https://biblio-assets.rosettastone.com/' + resourceId });
        }
        break;

      case 'lts':
        var ltsUri = 'https://ms5001.rosettastone.com/TestMediumHandler.ashx';
        ltsUri += '?DISC=' + splitMediaUrn[2];
        ltsUri += '&SET=' + splitMediaUrn[3];
        ltsUri += '&TEST=' + splitMediaUrn[4];
        ltsUri += '&ID=' + splitMediaUrn[5];
        if (mediaType === MEDIA_TYPE.AUDIO) {
          ltsUri += '&EXT=SNF';
        } else if (mediaType === MEDIA_TYPE.IMAGE) {
          ltsUri += '&EXT=jpg';
        }
        infoArray.push({ mediaUri: ltsUri });
        break;

      default:
        errorHandler('invalid urn type: ' + urn.media_uri);
        return null;
    }
  }
  return infoArray;
};

// transcript helper
export var getMsFromTimestamp = function getMsFromTimestamp(timestamp) {
  var results = timestamp ? timestamp.match(/(\d+):(\d+)\.(\d+)/) : null;
  if (results && results.length === 4) {
    return parseInt(results[1], 10) * 1000 * 60 + parseInt(results[2], 10) * 1000 + parseInt(results[3], 10);
  }
  return -1;
};

// transcript helper
export var getLinesFromTranscript = function getLinesFromTranscript(transcript) {
  var lines = (transcript || '').split(/[\n\r]/);
  var parsedLines = lines.map(function (line) {
    var results = line.match(/(\[(\d+:\d+\.\d+)])?(.*)/);
    return {
      timestamp: results[2] || '',
      text: results[3] || ''
    };
  });
  return parsedLines;
};

// not sure why we sort them but it should be cheap
export var getSortedLinesFromTranscript = function getSortedLinesFromTranscript(transcript) {
  if (!transcript) {
    return [];
  }
  var lines = getLinesFromTranscript(transcript);
  lines.forEach(function (line) {
    line.ms = getMsFromTimestamp(line.timestamp);
  });
  var sortedLines = lines.sort(function (lineA, lineB) {
    if (lineA.ms <= lineB.ms) {
      return -1;
    }
    return 1;
  });
  return sortedLines;
};

// Audio autoplay is disabled by default in the new browsers so this is a workaround.
//  If the user clicks anywhere, we play a 15ms silent sound.
//  After audio is played once, autoplay will work.
//  We are also using Howler's workaround.  Our workaround seems to make
//  the first-autoplayed sound work, and Howler's takes it after that
//
export var initAudioAutoplayWorkaround = function initAudioAutoplayWorkaround() {
  Howler.autoUnlock = true;

  var body = document.getElementsByTagName('body')[0];
  function bodyOnClickListener() {
    body.removeEventListener('click', bodyOnClickListener, true);
    var sound = new Howl({
      src: [silence],
      format: ['mp3'],
      html5: true
    });
    sound.play();
  }
  body.addEventListener('click', bodyOnClickListener, true);
};